import { useEffect, useState } from "react";
import { useSharedValue } from "react-native-reanimated";
import GatheringPrivacy from "src/api/graphql/__generated__/enums/GatheringPrivacy";
import GatheringViewMethod from "src/api/graphql/__generated__/enums/GatheringViewMethod";
import { NativeHeaderGradient } from "src/components/GatheringHeader";
import MarkGatheringViewed from "src/components/MarkGatheringViewed";
import NativeBackgroundColorWrapper from "src/components/NativeBackgroundColorWrapper";
import { PaletteWrapper } from "src/contexts/PaletteContext";
import { PublicGatheringModalConfig } from "src/modals/configs/PublicGatheringModal";
import RootModalContainer from "src/modals/containers/RootModalContainer";
import { GatheringPreviewSubPageProps } from "../../types";
import GatheringPublicFullBody from "./components/GatheringPublicFullBody";
import GatheringPublicFullFooter from "./components/GatheringPublicFullFooter";
import GatheringPublicFullPopUp from "./components/GatheringPublicFullPopUp";
import GatheringPublicFullPresentPhotoDetail from "./components/GatheringPublicFullPresentPhotoDetail";
import GatheringPublicFullContext from "./contexts/GatheringPublicFullContext";
import useGatheringInfoFullPublic from "./hooks/useGatheringInfoFullPublic";
import { createGatheringPublicFullStore } from "./stores/gatheringPublicFullStore";

const GatheringPublicFull = ({
	gathering: { refCode, name, gatheringId, palette, stickerPack, thumbnail },
	handleJoinGathering,
	joinLoading,
	overrideNoPublicPreview,
}: GatheringPreviewSubPageProps & {
	overrideNoPublicPreview?: () => void;
}) => {
	const { gathering } = useGatheringInfoFullPublic(gatheringId, {
		fetchPolicy: "cache-and-network",
	});

	useEffect(() => {
		// If the web cache is out of date and this actually isn't a previewable album, then we want to fallback to private
		if (gathering?.privacy && !gathering?.privacy?.includes(GatheringPrivacy.FullPublic)) {
			overrideNoPublicPreview?.();
		}
	}, [gathering?.privacy, overrideNoPublicPreview]);

	const [store] = useState(() =>
		createGatheringPublicFullStore({
			refCode,
			gatheringId,
			handleJoinGathering,
			joinLoading,
			gatheringName: name ?? gathering?.name ?? undefined,
			privacy: gathering?.privacy ?? [],
		}),
	);
	useEffect(() => {
		store.setState({
			handleJoinGathering,
			joinLoading,
			gatheringName: name ?? gathering?.name ?? undefined,
			privacy: gathering?.privacy ?? [],
		});
	}, [name, gathering?.name, handleJoinGathering, joinLoading, store, gathering?.privacy]);

	const scrollOffset = useSharedValue(0);

	return (
		<>
			<MarkGatheringViewed
				gatheringId={gatheringId}
				method={GatheringViewMethod.FullPublic}
			/>
			<GatheringPublicFullPresentPhotoDetail gatheringId={gatheringId} />
			<PaletteWrapper palette={palette} syncBackgroundColor>
				<NativeBackgroundColorWrapper>
					<GatheringPublicFullContext.Provider value={{ store }}>
						<GatheringPublicFullPopUp />
						<NativeHeaderGradient />
						<GatheringPublicFullBody
							scrollOffset={scrollOffset}
							stickerPack={stickerPack}
							thumbnail={thumbnail}
						/>
						<GatheringPublicFullFooter />
					</GatheringPublicFullContext.Provider>
				</NativeBackgroundColorWrapper>
			</PaletteWrapper>
			<RootModalContainer config={PublicGatheringModalConfig} />
		</>
	);
};
export default GatheringPublicFull;
